import React from 'react';
import { Link } from 'react-router-dom';
import { User, BookOpen, Image, Heart, MessageCircle, Settings, LogOut } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import PadaidLogo from '../../components/ui/PadaidLogo';

const MemberDashboard = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/90 to-primary/5">
      {/* Header */}
      <header className="bg-card/50 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <PadaidLogo size="md" showText={true} />
              <div className="ml-4">
                <span className="text-sm text-muted-foreground">Espace</span>
                <h1 className="text-lg font-semibold text-primary">Membre</h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm">
                <Settings className="h-4 w-4 mr-2" />
                Paramètres
              </Button>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/">
                  <LogOut className="h-4 w-4 mr-2" />
                  Déconnexion
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-foreground mb-2">
            Bienvenue dans votre espace membre
          </h2>
          <p className="text-muted-foreground">
            Accédez à vos ressources PADAID et participez à notre mission humanitaire
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="hover:shadow-lg transition-shadow border-primary/20">
            <CardHeader className="text-center">
              <BookOpen className="h-8 w-8 text-primary mx-auto mb-2" />
              <CardTitle className="text-lg">Articles</CardTitle>
              <CardDescription>Consultez les derniers tracts et analyses</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full bg-primary hover:bg-primary/90" asChild>
                <Link to="/blog">Lire les articles</Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow border-secondary/20">
            <CardHeader className="text-center">
              <Image className="h-8 w-8 text-secondary mx-auto mb-2" />
              <CardTitle className="text-lg">Galerie</CardTitle>
              <CardDescription>Photos et vidéos de nos missions</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full bg-secondary hover:bg-secondary/90 text-black" asChild>
                <Link to="/gallery">Voir la galerie</Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow border-accent/20">
            <CardHeader className="text-center">
              <Heart className="h-8 w-8 text-accent mx-auto mb-2" />
              <CardTitle className="text-lg">Dons</CardTitle>
              <CardDescription>Soutenez nos actions sur le terrain</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full bg-accent hover:bg-accent/90" asChild>
                <Link to="/donate">Faire un don</Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow border-destructive/20">
            <CardHeader className="text-center">
              <MessageCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
              <CardTitle className="text-lg">Contact</CardTitle>
              <CardDescription>Restez en contact avec l'équipe</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full bg-destructive hover:bg-destructive/90" asChild>
                <Link to="/contact">Nous contacter</Link>
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Member Benefits */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <User className="mr-2 h-5 w-5 text-primary" />
                Avantages Membre
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-primary rounded-full mr-3"></div>
                  <span>Accès aux articles et tracts exclusifs</span>
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-primary rounded-full mr-3"></div>
                  <span>Participation aux événements PADAID</span>
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-primary rounded-full mr-3"></div>
                  <span>Newsletter mensuelle avec rapports d'activité</span>
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-primary rounded-full mr-3"></div>
                  <span>Accès prioritaire aux nouveaux programmes</span>
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-primary rounded-full mr-3"></div>
                  <span>Espace de discussion avec la communauté</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Activité Récente</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium">Nouvel article publié</p>
                    <p className="text-xs text-muted-foreground">L'importance de l'aide humanitaire en Afrique</p>
                    <p className="text-xs text-muted-foreground">Il y a 2 heures</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-secondary rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium">Nouvelles photos ajoutées</p>
                    <p className="text-xs text-muted-foreground">Mission au Sénégal - Construction d'écoles</p>
                    <p className="text-xs text-muted-foreground">Il y a 1 jour</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-accent rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium">Événement à venir</p>
                    <p className="text-xs text-muted-foreground">Assemblée générale des membres</p>
                    <p className="text-xs text-muted-foreground">Dans 5 jours</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Call to Action */}
        <Card className="mt-8 bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <div className="text-center">
              <h3 className="text-xl font-semibold mb-2">Passez au niveau supérieur</h3>
              <p className="text-muted-foreground mb-4">
                Devenez Supporter ou Ambassadeur pour débloquer plus d'avantages
              </p>
              <Button className="bg-primary hover:bg-primary/90" asChild>
                <Link to="/membership">Découvrir les plans</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MemberDashboard;